// ── AchievementManager ──
// Singleton that evaluates achievement progress, unlocks tiers, grants rewards.
// Listens to EventBus for key progression events to trigger re-evaluation.

import { ACHIEVEMENT_DEFS, AchievementDef, AchievementReward, AchievementTier, TIER_COLORS } from '@/config/AchievementDefs';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { ResourceManager } from '@/systems/ResourceManager';
import { SaveManager } from '@/systems/SaveManager';
import { SlotManager } from '@/systems/SlotManager';
import { EventBus, GameEvents } from '@/events/EventBus';
import { EnemyType, EnemyGrade, FLYING_ENEMY_TYPES, ENEMY_DAMAGE_MULTIPLIER } from '@/config/GameConfig';
import { getEasterEggStat } from '@/systems/EasterEggs';

export interface AchievementProgress {
  def: AchievementDef;
  currentValue: number;
  unlocked: boolean;            // true if threshold met
  isRevealed: boolean;          // true if not hidden or already unlocked
}

class AchievementManagerClass {
  private initialized = false;
  /** Achievements unlocked but not yet displayed as notifications (consumed by TitleScene on create) */
  private _pendingNotifications: { id: string; badge: string; def: AchievementDef }[] = [];

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Listen to key events that can trigger new achievements
    EventBus.on(GameEvents.KILLS_UPDATED, () => this.checkAll());
    EventBus.on(GameEvents.LEVEL_COMPLETED, () => this.checkAll());
    EventBus.on(GameEvents.COMPONENT_UNLOCKED, () => this.checkAll());
    EventBus.on(GameEvents.COMPONENT_EVOLVED, () => this.checkAll());
    EventBus.on(GameEvents.BASE_UPGRADE_CHANGED, () => this.checkAll());
  }

  /** Evaluate all achievements against current stats. Unlocks and grants rewards. */
  checkAll(): void {
    if (SlotManager.activeSlot < 0) return; // no save — no tracking
    for (const def of ACHIEVEMENT_DEFS) {
      const value = this.getStatValue(def.stat);
      const alreadyUnlocked = ProgressionManager.getAchievementTier(def.id) >= 1;
      if (alreadyUnlocked) continue;

      // Skip achievements that require Infernal difficulty (fog of war active)
      if (def.requireInfernal && ENEMY_DAMAGE_MULTIPLIER < 3.0) continue;

      if (value >= def.threshold) {
        // Unlock achievement — reward is NOT auto-granted, player must claim in STATS
        ProgressionManager.setAchievementTier(def.id, 1);
        const payload = { id: def.id, badge: def.badge, def };
        this._pendingNotifications.push(payload);
        EventBus.emit(GameEvents.ACHIEVEMENT_UNLOCKED, payload);
      }
    }
  }

  /** Get the rewards array for a given achievement (falls back to legacy currency) */
  getRewards(def: AchievementDef): AchievementReward[] {
    return def.rewards ?? [{ type: 'currency', amount: def.reward }];
  }

  /** Claim reward for an unlocked achievement. Returns the rewards granted. */
  claimReward(id: string): AchievementReward[] | null {
    const def = ACHIEVEMENT_DEFS.find(a => a.id === id);
    if (!def) return null;
    // Must be unlocked and not yet claimed
    if (ProgressionManager.getAchievementTier(def.id) < 1) return null;
    if (ProgressionManager.getAchievementClaimedReward(def.id) >= 1) return null;

    const rewards = this.getRewards(def);
    for (const r of rewards) {
      switch (r.type) {
        case 'currency': ProgressionManager.addCurrency(r.amount ?? 0); break;
        case 'electro': ResourceManager.addElectro(r.amount ?? 0); break;
        case 'connardium': ResourceManager.addCo(r.amount ?? 0); break;
        case 'perk': if (r.perkId) ProgressionManager.unlockPerk(r.perkId); break;
      }
    }
    ProgressionManager.setAchievementClaimedReward(id, 1);
    EventBus.emit(GameEvents.REWARD_CLAIMED, { id, rewards });
    return rewards;
  }

  /** Check if a reward is claimable (unlocked but not yet claimed) */
  isRewardClaimable(id: string): boolean {
    return ProgressionManager.getAchievementTier(id) >= 1 &&
           ProgressionManager.getAchievementClaimedReward(id) < 1;
  }

  /** Consume pending notifications (for scenes that missed the real-time event). */
  consumePendingNotifications(): { id: string; badge: string; def: AchievementDef }[] {
    const pending = [...this._pendingNotifications];
    this._pendingNotifications = [];
    return pending;
  }

  /** Count of unclaimed rewards */
  getUnclaimedCount(): number {
    let count = 0;
    for (const def of ACHIEVEMENT_DEFS) {
      if (this.isRewardClaimable(def.id)) count++;
    }
    return count;
  }

  /** Get progress info for all achievements (for UI display) */
  getAllProgress(): AchievementProgress[] {
    return ACHIEVEMENT_DEFS.map(def => this._progress(def));
  }

  /** Get progress for a single achievement */
  getProgress(id: string): AchievementProgress | undefined {
    const def = ACHIEVEMENT_DEFS.find(d => d.id === id);
    if (!def) return undefined;
    return this._progress(def);
  }

  private _progress(def: AchievementDef): AchievementProgress {
    const value = this.getStatValue(def.stat);
    const unlocked = ProgressionManager.getAchievementTier(def.id) >= 1;
    const isRevealed = !def.hidden || unlocked;
    return { def, currentValue: value, unlocked, isRevealed };
  }

  /** Resolve a stat key to its current numeric value */
  getStatValue(stat: string): number {
    // Kill-based stats
    if (stat === 'totalKills') return ProgressionManager.getTotalKills();

    if (stat.startsWith('kills:')) {
      const subKey = stat.slice(6); // e.g. "grunt", "tank", "flying", "elite"
      const kills = ProgressionManager.getKillsByType();
      const killsByGrade = ProgressionManager.getKillsByTypeGrade();

      if (subKey === 'flying') {
        let sum = 0;
        for (const type of Object.values(EnemyType)) {
          if (FLYING_ENEMY_TYPES.has(type)) sum += (kills[type] ?? 0);
        }
        return sum;
      }

      if (subKey === 'elite') {
        // Sum all kills of elite+ grades across all types
        let sum = 0;
        const eliteGrades = [EnemyGrade.ELITE, EnemyGrade.BOSS, EnemyGrade.SUPER, EnemyGrade.ULTRA, EnemyGrade.GOLD];
        for (const type of Object.values(EnemyType)) {
          for (const grade of eliteGrades) {
            sum += (killsByGrade[`${type}:${grade}`] ?? 0);
          }
        }
        return sum;
      }

      // Direct type match (e.g. "grunt", "tank")
      return kills[subKey] ?? 0;
    }

    // Cumulative mission stats
    const cs = ProgressionManager.getCumulativeStats();
    switch (stat) {
      case 'fogExplored':     return cs.fogExplored;
      case 'robotsDeployed':  return cs.robotsDeployed;
      case 'robotsLost':      return cs.robotsLost;
      case 'basesDestroyed':  return cs.basesDestroyed;
      case 'perfectWaves':    return cs.perfectWaves;
      case 'bestStreak':      return cs.bestStreak;
      case 'totalDistance':    return Math.round(cs.totalDistance);
    }

    // Precision (percentage)
    if (stat === 'precision') {
      return cs.shotsFired > 0 ? Math.round((cs.shotsHit / cs.shotsFired) * 100) : 0;
    }

    // Progression stats
    switch (stat) {
      case 'levelsCompleted':    return ProgressionManager.getCompletedCount();
      case 'levelsUnlocked':     return ProgressionManager.getUnlockedCount();
      case 'totalStars':         return ProgressionManager.getTotalStars();
      case 'componentsUnlocked': return Math.max(0, ProgressionManager.getUnlockedComponents().size - 5); // exclude 5 starting components
      case 'totalEvolutions':    return ProgressionManager.getTotalEvolutions();
      case 'totalReruns':        return ProgressionManager.getTotalReruns();
      case 'achievementTiers':   return ProgressionManager.getTotalAchievementTiers();
    }

    // Platinum count (from score history)
    if (stat === 'platinumCount') {
      const seen = new Set<string>();
      for (const s of SaveManager.getScoreHistory()) {
        if (s.isPlatinum) seen.add(s.levelId);
      }
      return seen.size;
    }

    // Mastery stats
    if (stat === 'maxEvoComponents') return ProgressionManager.getMaxEvoComponentCount();
    if (stat === 'totalSpecialRes') return ResourceManager.co + ResourceManager.electro;
    if (stat === 'speedrunWins') return cs.speedrunWins;
    if (stat === 'minimalistWins') return cs.minimalistWins;

    // ── Funny/weird achievement stats ──
    if (stat === 'winsAllRobotsDead') return cs.winsAllRobotsDead;
    if (stat === 'winsBaseUnder5Pct') return cs.winsBaseUnder5Pct;
    if (stat === 'winsLost10Plus') return cs.winsLost10Plus;
    if (stat === 'winsNoDamage') return cs.winsNoDamage;
    if (stat === 'winsOnlyFixed') return cs.winsOnlyFixed;
    if (stat === 'lostWave1') return cs.lostWave1;
    if (stat === 'winsWave1') return cs.winsWave1;
    if (stat === 'winsUnder10s') return cs.winsUnder10s;
    if (stat === 'maxRobotsLostOneMission') return cs.maxRobotsLostOneMission;
    if (stat === 'afkLosses') return cs.afkLosses;
    if (stat === 'winsSupportOnly') return cs.winsSupportOnly;
    if (stat === 'winsNoFlying') return cs.winsNoFlying;
    if (stat === 'winsOnlyFlying') return cs.winsOnlyFlying;
    if (stat === 'mineKills') return cs.mineKills;
    if (stat === 'camperBaseKills') return cs.camperBaseKills;

    if (stat === 'cheatedWins') return cs.cheatedWins;

    // ── New achievement stats ──
    if (stat === 'maxDamageSurvived') return cs.maxDamageSurvived;
    if (stat === 'maxSingleHitDamage') return cs.maxSingleHitDamage;
    if (stat === 'maxCombosOneWave') return cs.maxCombosOneWave;
    if (stat === 'totalRicochets') return cs.totalRicochets;
    if (stat === 'maxPrecisionMission') return cs.maxPrecisionMission;

    // ── Easter egg achievement stats (stored separately in EasterEggs module) ──

    if (stat.startsWith('easterEgg:')) {
      const eeStat = stat.slice(10);
      return getEasterEggStat(eeStat);
    }

    return 0;
  }
}

/** Singleton instance */
export const AchievementManager = new AchievementManagerClass();
